import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import MovieCard from "@/components/feature/MovieCard";
import { useSeo } from "@/hooks/useSeo";
import { useFavorites } from "@/hooks/useFavorites";
import { useToast } from "@/components/feature/ToastProvider";
import { movies } from "@/mocks/movies";

export default function Detail() {
  const { id } = useParams();
  const movieId = Number(id);
  const movie = movies.find((m) => m.id === movieId);
  const { isFavorite, toggle } = useFavorites();
  const { showToast } = useToast();

  const related = useMemo(() => {
    if (!movie) return [];
    const sameGenre = movies.filter(
      (m) => m.id !== movie.id && m.genres.some((g) => movie.genres.includes(g))
    );
    const shuffled = [...sameGenre].sort(() => Math.random() - 0.5);
    if (shuffled.length >= 5) return shuffled.slice(0, 5);
    const fillers = movies
      .filter((m) => m.id !== movie.id && !shuffled.includes(m))
      .sort((a, b) => b.hot - a.hot);
    return [...shuffled, ...fillers].slice(0, 5);
  }, [movie]);

  useSeo(
    movie ? `${movie.title} - 看片网站影视详情` : "影视详情 - 看片网站",
    movie
      ? `${movie.title}，年份${movie.year}，类型${movie.genres.join("、")}，免费在线观看与详情介绍。`
      : "看片网站影视详情页，提供影片类型、地区、年份、简介等详细信息。",
    movie ? `${movie.title},影视详情,${movie.genres.join(",")},看片网站` : "影视详情,看片网站"
  );

  if (!movie) {
    return (
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-center px-4 py-24 text-center md:px-6">
        <i className="ri-file-unknow-line text-5xl text-foreground-400"></i>
        <h1 className="mt-4 font-heading text-2xl font-black text-foreground-950">未找到该影视作品</h1>
        <p className="mt-2 text-sm text-foreground-500">它可能已被移除，或链接不正确</p>
        <Link
          to="/category"
          className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-apps-2-line leading-none"></i>
          去分类页逛逛
        </Link>
      </div>
    );
  }

  const favorited = isFavorite(movie.id);

  const handleFavorite = () => {
    const now = toggle(movie.id);
    showToast(now ? "已加入收藏" : "已取消收藏", now ? "ri-heart-3-fill" : "ri-heart-3-line");
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      showToast("链接已复制，快去分享吧", "ri-link");
    } catch {
      showToast("复制失败，请手动复制地址栏链接", "ri-error-warning-line");
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 md:px-6 md:py-8">
      <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑">
        <Link to="/" className="hover:text-primary-600">首页</Link>
        <i className="ri-arrow-right-s-line leading-none"></i>
        <Link to="/category" className="hover:text-primary-600">影视分类</Link>
        <i className="ri-arrow-right-s-line leading-none"></i>
        <Link to={`/category?genre=${encodeURIComponent(movie.genres[0])}`} className="hover:text-primary-600">
          {movie.genres[0]}
        </Link>
        <i className="ri-arrow-right-s-line leading-none"></i>
        <span className="text-foreground-900">{movie.title}</span>
      </nav>

      <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-[320px_1fr] lg:gap-10">
        <div className="mx-auto w-full max-w-[220px] sm:max-w-[260px] lg:mx-0 lg:max-w-none">
          <div className="overflow-hidden rounded-lg border border-background-200/70 bg-background-200">
            <img
              src={movie.image}
              alt={`${movie.title} 影视封面`}
              title={`${movie.title} 影视资源封面`}
              className="aspect-[5/7] w-full object-top"
            />
          </div>
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={handleFavorite}
              className={`flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-3 text-sm font-bold transition-colors ${
                favorited
                  ? "bg-accent-500 text-primary-950 hover:bg-accent-600"
                  : "bg-primary-500 text-background-50 hover:bg-primary-600"
              }`}
            >
              <i className={`${favorited ? "ri-heart-3-fill" : "ri-heart-3-line"} text-base leading-none`}></i>
              {favorited ? "已收藏" : "收藏"}
            </button>
            <button
              type="button"
              onClick={handleShare}
              aria-label="分享链接"
              className="flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-md border border-background-300/70 bg-background-50 text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              <i className="ri-share-forward-line text-lg leading-none"></i>
            </button>
          </div>
        </div>

        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-md bg-accent-500 px-2.5 py-1 text-xs font-bold text-primary-950">{movie.type}</span>
            <span className="flex items-center gap-1 rounded-md bg-primary-50 px-2.5 py-1 text-xs font-bold text-primary-700">
              <i className="ri-star-fill text-[10px] leading-none"></i>
              {movie.rating.toFixed(1)} 分
            </span>
            <span className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-900">
              {movie.region} · {movie.year}
            </span>
          </div>

          <h1 className="mt-4 font-heading text-3xl font-black leading-tight text-foreground-950 md:text-4xl">
            {movie.title}
          </h1>

          <div className="mt-4 flex flex-wrap gap-2">
            {movie.genres.map((g) => (
              <Link
                key={g}
                to={`/category?genre=${encodeURIComponent(g)}`}
                className="whitespace-nowrap rounded-full border border-background-200/70 bg-background-50 px-3 py-1 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                {g}
              </Link>
            ))}
          </div>

          <dl className="mt-6 grid grid-cols-1 gap-x-8 gap-y-3 border-y border-background-200/70 py-5 sm:grid-cols-2">
            <div className="flex gap-2 text-sm">
              <dt className="shrink-0 text-foreground-500">导演</dt>
              <dd className="font-medium text-foreground-900">{movie.director}</dd>
            </div>
            <div className="flex gap-2 text-sm">
              <dt className="shrink-0 text-foreground-500">{movie.type === "电视剧" ? "集数" : "时长"}</dt>
              <dd className="font-medium text-foreground-900">
                {movie.type === "电视剧" ? `${movie.episodes} 集` : `${movie.episodes} 分钟`}
              </dd>
            </div>
            <div className="flex gap-2 text-sm sm:col-span-2">
              <dt className="shrink-0 text-foreground-500">主演</dt>
              <dd className="font-medium text-foreground-900">{movie.actors.join(" / ")}</dd>
            </div>
            <div className="flex gap-2 text-sm">
              <dt className="shrink-0 text-foreground-500">更新时间</dt>
              <dd className="font-medium text-foreground-900">{movie.updated}</dd>
            </div>
            <div className="flex gap-2 text-sm">
              <dt className="shrink-0 text-foreground-500">所属分类</dt>
              <dd className="font-medium">
                <Link to={`/category?type=${encodeURIComponent(movie.type)}`} className="text-primary-600 hover:text-primary-700">
                  {movie.type}
                </Link>
              </dd>
            </div>
          </dl>

          <section className="mt-6">
            <h2 className="font-heading text-lg font-bold text-foreground-950">剧情简介</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700 md:text-base">{movie.synopsis}</p>
          </section>

          <p className="mt-5 rounded-lg border border-background-200/70 bg-secondary-50 px-4 py-3 text-xs leading-relaxed text-foreground-600">
            <i className="ri-information-line mr-1 text-secondary-600 leading-none"></i>
            本页面仅提供影视作品信息展示与检索，不提供在线播放与下载服务。
          </p>
        </div>
      </div>

      <section className="mt-12">
        <div className="mb-4 flex items-center gap-2 border-l-4 border-accent-500 pl-4">
          <h2 className="font-heading text-xl font-black text-foreground-950">相关推荐</h2>
          <span className="text-xs text-foreground-500">根据「{movie.genres[0]}」类型为你推荐</span>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-5">
          {related.map((item) => (
            <MovieCard key={item.id} movie={item} />
          ))}
        </div>
      </section>
    </div>
  );
}